import { Flex, InputNumber, Typography, theme } from 'antd';

import { buildUpdateClassPatch } from '../../utils/classMutations';
import ColorDot from './ColorDot';

const { Text } = Typography;

/**
 * Simple (1D) classification editor: each class's [min, max) range over the
 * filter's property. Edits the `filter` config via `updateFilter` (partial merge) —
 * the parent owns the config and pushes it to the store for live map coloring.
 *
 * Scope: ranges only, the same split MatrixEditor has with its threshold grid.
 * The property picker sits in the drawer header (FilterEditorHeader) alongside
 * the matrix axes; only its data-range hint is shown here, since it is what the
 * ranges below are read against. Class identity (color, label), pricing,
 * currency and add/remove all live in CostSummary in the pane beside this one,
 * so they are edited in exactly one place; this editor shows a class's dot and
 * name read-only so a range can be matched to its row over there. Adding a class in CostSummary seeds it with an
 * open [0, ∞) range that gets narrowed here.
 *
 * A null `max` means open-ended (matches classifyWithSimpleFilter), shown as ∞.
 *
 * @param {object} props
 * @param {object} props.filter - simple filter config
 * @param {(updates: object) => void} props.updateFilter
 * @param {Array<{id,label,unit}>} props.availableProperties
 * @param {Object} [props.classificationResults] - per-class { count, ... } from
 *   useHexagonClassification, used for the "N <unitLabel>" hint per class
 * @param {{min:number,max:number,hasData:boolean}} [props.propertyRange] - observed
 *   data range for the selected property (hint text only)
 * @param {string} [props.coverageMessage] - warning when ranges leave data unclassified
 * @param {string} [props.unitLabel='hexagons'] - what a classified unit is called
 *   in the per-class count hint ('plots' for the DSP filter)
 */
const SimpleEditor = ({
	filter,
	updateFilter,
	availableProperties,
	classificationResults = {},
	propertyRange,
	coverageMessage,
	unitLabel = 'hexagons',
}) => {
	const { token } = theme.useToken();
	const classes = filter.classes ?? [];
	const propertyLabel = availableProperties.find(p => p.id === filter.property)?.label;

	const updateClass = (index, patch) => updateFilter(buildUpdateClassPatch(filter, index, patch));

	const rowDivider = { borderBottom: `1px solid ${token.colorBorderSecondary}` };

	return (
		<Flex vertical gap={8}>
			<Flex vertical gap={8}>
				<Flex justify="space-between" align="baseline" gap={8} wrap="wrap">
					<Text strong style={{ fontSize: '0.8rem' }}>
						Ranges
					</Text>
					{propertyRange && (
						<Text
							type={propertyRange.hasData ? 'secondary' : 'warning'}
							style={{ fontSize: '0.75rem' }}>
							{propertyRange.hasData
								? `Data range: ${propertyRange.min} – ${propertyRange.max}${propertyLabel ? ` (${propertyLabel})` : ''}`
								: '⚠ No data available for this property'}
						</Text>
					)}
				</Flex>
				{coverageMessage && (
					<Text type="warning" style={{ fontSize: '0.72rem' }}>
						⚠ {coverageMessage}
					</Text>
				)}

				{classes.length === 0 && (
					<Text type="secondary" style={{ fontSize: '0.75rem' }}>
						No classes yet — add one in the cost summary to set its range here.
					</Text>
				)}

				{/* One row per class: identity read-only on the left, the range on the
				    right. Rows line up as a table so ranges can be compared down the
				    column, and each row's label and count share the remaining width. */}
				{classes.map((cls, index) => (
					<Flex
						key={cls.id}
						align="center"
						gap={8}
						style={{
							padding: '4px 0',
							...(index < classes.length - 1 ? rowDivider : {}),
						}}>
						<Flex vertical gap={0} style={{ flex: 1, minWidth: 0 }}>
							<Text
								ellipsis={{ tooltip: cls.label }}
								style={{ fontSize: '0.78rem', display: 'block' }}>
								<ColorDot color={cls.color} />
								{cls.label}
							</Text>
							<Text type="secondary" style={{ fontSize: '0.7rem', paddingLeft: 16 }}>
								{classificationResults[cls.id]?.count || 0} {unitLabel}
							</Text>
						</Flex>
						<InputNumber
							size="small"
							value={cls.min}
							onChange={min => updateClass(index, { min })}
							placeholder="0"
							prefix={
								<Text type="secondary" style={{ fontSize: '0.7rem' }}>
									min
								</Text>
							}
							style={{ width: 110 }}
							aria-label={`Minimum for ${cls.label}`}
						/>
						<InputNumber
							size="small"
							value={cls.max}
							onChange={max => updateClass(index, { max })}
							placeholder="∞"
							prefix={
								<Text type="secondary" style={{ fontSize: '0.7rem' }}>
									max
								</Text>
							}
							style={{ width: 110 }}
							aria-label={`Maximum for ${cls.label} (empty = no limit)`}
						/>
					</Flex>
				))}
			</Flex>
		</Flex>
	);
};

export default SimpleEditor;
